import React, { useState, useEffect, useRef } from 'react';
import { LogIn, Shield, UserCircle, Smartphone, Download, Monitor, Twitch } from 'lucide-react';
import { loginWithGoogle, loginAnonymously, loginWithEmail, registerWithEmail, loginWithTwitch } from '../supabase';
import { motion } from 'motion/react';

interface LandingPageProps {
  onLoginSuccess: (isNewUser: boolean) => void;
}

export default function LandingPage({ onLoginSuccess }: LandingPageProps) {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'home' | 'signup' | 'login'>('home');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const requestFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen mode: ${err.message}`);
      });
    }
  };

  const handleLogin = async (type: 'google' | 'guest' | 'twitch') => {
    setIsLoading(type);
    setError(null);
    try {
      let result;
      if (type === 'google') {
        result = await loginWithGoogle();
      } else if (type === 'twitch') {
        result = await loginWithTwitch();
      } else {
        result = await loginAnonymously();
      }
      onLoginSuccess(result.isNewUser);
    } catch (err: any) {
      console.error("Login failed:", err);
      setError(err.message || "Failed to log in. Please try again.");
    } finally {
      setIsLoading(null);
    }
  };

  const handleEmailAuth = async (isSignUp: boolean) => {
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setIsLoading('email');
    setError(null);
    try {
      let result;
      if (isSignUp) {
        result = await registerWithEmail(email, password);
      } else {
        result = await loginWithEmail(email, password);
      }
      onLoginSuccess(result.isNewUser);
    } catch (err: any) {
      console.error("Auth failed:", err);
      setError(err.message || "Authentication failed.");
    } finally {
      setIsLoading(null);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let particles: Particle[] = [];
    const particleCount = 75;
    const maxDistance = 130;
    let mouse = { x: null as number | null, y: null as number | null, radius: 150 };
    let hue = 0;
    let animationFrameId: number;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
      }
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    resize();

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;

      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.vx = (Math.random() - 0.5) * 0.6;
        this.vy = (Math.random() - 0.5) * 0.6;
        this.r = Math.random() * 1.8 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas!.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas!.height) this.vy *= -1;

        if (mouse.x !== null && mouse.y !== null) {
          const dx = this.x - mouse.x;
          const dy = this.y - mouse.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < mouse.radius) {
            this.x += dx / 18;
            this.y += dy / 18;
          }
        }
      }

      draw() {
        const c = `hsl(${hue}, 100%, 60%)`;
        ctx!.beginPath();
        ctx!.arc(this.x, this.y, this.r, 0, Math.PI * 2);
        ctx!.fillStyle = c;
        ctx!.shadowBlur = 14;
        ctx!.shadowColor = c;
        ctx!.fill();
        ctx!.shadowBlur = 0;
      }
    }

    const init = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) particles.push(new Particle());
    };

    const connect = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxDistance) {
            const op = (1 - dist / maxDistance) * 0.6;
            ctx!.beginPath();
            ctx!.moveTo(particles[i].x, particles[i].y);
            ctx!.lineTo(particles[j].x, particles[j].y);
            ctx!.strokeStyle = `hsla(${hue}, 100%, 60%, ${op})`;
            ctx!.lineWidth = 0.6;
            ctx!.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx!.clearRect(0, 0, canvas.width, canvas.height);
      hue += 0.4;
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      connect();
      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#05070A] text-[#E2E8F0] font-sans overflow-x-hidden selection:bg-cyan-500 selection:text-black">
      <style>{`
        :root {
            --cyan-glow: rgba(0, 245, 255, 0.8);
            --green-glow: rgba(0, 255, 157, 0.8);
            --color-green: #00ff9d;
            --color-cyan: #00f5ff;
            --bg-color: #05070A;
            --card-bg: rgba(16, 21, 32, 0.5);
        }

        .font-space {
            font-family: 'Space Grotesk', sans-serif;
            letter-spacing: -0.02em;
        }

        .glass-card {
            background: var(--card-bg);
            backdrop-filter: blur(12px);
            border: 1px solid rgba(255,255,255,0.1);
            transition: 0.3s ease;
        }

        .glass-card:hover {
            transform: translateY(-8px);
            border-color: var(--cyan-glow);
            box-shadow: 0 0 30px rgba(0,245,255,0.2);
        }

        .btn-gradient {
            background: linear-gradient(to right, var(--color-green), var(--color-cyan));
            color: #05070A;
            transition: all 0.3s ease;
        }

        .btn-gradient:hover:not(:disabled) {
            transform: scale(1.05);
            box-shadow: 0 0 25px var(--cyan-glow);
        }

        .btn-outline-gradient {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(5, 7, 10, 0.9);
            color: #e2e8f0;
            border-radius: 0.75rem;
            border: 1px solid rgba(255,255,255,0.08);
            overflow: hidden;
            transition: all 0.25s ease;
            isolation: isolate;
        }

        .btn-outline-gradient::before {
            content: '';
            position: absolute;
            inset: 0;
            padding: 2px;
            border-radius: inherit;
            background: linear-gradient(135deg, #00ff9d, #00f5ff);
            -webkit-mask: 
                linear-gradient(#000 0 0) content-box, 
                linear-gradient(#000 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            pointer-events: none;
            opacity: 0.9;
        }

        .btn-outline-gradient:hover:not(:disabled) {
            transform: scale(1.05);
            box-shadow: 0 0 25px rgba(0,245,255,0.25);
            color: #ffffff;
        }

        .loader {
          width: 20px;
          height: 20px;
          border: 2px solid currentColor;
          border-bottom-color: transparent;
          border-radius: 50%;
          display: inline-block;
          animation: rotation 1s linear infinite;
        }

        .btn-twitch {
            background: #9146FF;
            color: #ffffff;
            transition: all 0.25s ease;
        }

        .btn-twitch:hover:not(:disabled) {
            transform: scale(1.05);
            box-shadow: 0 0 25px rgba(145, 70, 255, 0.45);
            background: #a970ff;
        }

        @keyframes rotation {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <canvas ref={canvasRef} className="fixed inset-0 z-0" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(0,255,200,0.08),transparent_60%)] z-[1] pointer-events-none" />

      <main className="relative z-10">
        {/* HERO */}
        <section className="text-center pt-24 pb-32 px-6">
          <div className="max-w-4xl mx-auto flex flex-col items-center">
            {/* LOGO */}
            <div className="mb-12 w-full max-w-2xl">
              <img 
                src="https://i.ibb.co/b5yprKps/psydechat-logo-long-no-bg.png?v=2"
                alt="Psydechat Logo"
                className="w-full h-auto drop-shadow-[0_0_25px_rgba(0,245,255,0.5)]"
                style={{ display: 'block', margin: '0 auto', minHeight: '80px', objectFit: 'contain' }}
                referrerPolicy="no-referrer"
              />
            </div>

            {/* BUTTONS */}
            {view === 'home' && (
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={() => { requestFullscreen(); setView('login'); }}
                  disabled={!!isLoading}
                  className="font-space px-12 py-4 rounded-xl btn-gradient font-bold uppercase tracking-widest flex items-center justify-center gap-2 min-w-[200px]"
                >
                  {isLoading === 'google' ? <span className="loader" /> : <><LogIn size={18} /> Login</>}
                </button>
                <button 
                  onClick={() => { requestFullscreen(); setView('signup'); }}
                  disabled={!!isLoading}
                  className="font-space px-12 py-4 rounded-xl btn-outline-gradient font-bold uppercase tracking-widest flex items-center justify-center gap-2 min-w-[200px]"
                >
                  <UserCircle size={18} /> Sign Up
                </button>
              </div>
            )}
            
            {view === 'login' && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-[rgba(16,21,32,0.8)] backdrop-blur-xl border border-white/10 rounded-2xl p-8 mb-12"
              >
                <h2 className="text-2xl font-space font-bold mb-6 text-white text-left">Welcome back</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 text-left mb-2">Email Address</label>
                    <input 
                      type="email" 
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00f5ff] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 text-left mb-2">Password</label>
                    <input 
                      type="password" 
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00f5ff] transition-colors"
                    />
                  </div>
                  <button 
                    onClick={() => handleEmailAuth(false)}
                    disabled={!!isLoading}
                    className="w-full font-space py-3 rounded-xl btn-gradient font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    {isLoading === 'email' ? <span className="loader" /> : 'Log In'}
                  </button>
                  
                  <div className="relative py-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-[rgba(16,21,32,1)] text-white/40">Or continue with</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <button 
                      onClick={() => handleLogin('google')}
                      disabled={!!isLoading}
                      className="w-full font-space py-3 rounded-xl btn-outline-gradient font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                      {isLoading === 'google' ? <span className="loader" /> : <><LogIn size={18} /> Google</>}
                    </button>
                    <button 
                      onClick={() => handleLogin('twitch')}
                      disabled={!!isLoading}
                      className="w-full font-space py-3 rounded-xl btn-twitch font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                      {isLoading === 'twitch' ? <span className="loader" /> : <><Twitch size={18} /> Twitch</>}
                    </button>
                  </div>
                </div>
                
                <button 
                  onClick={() => setView('home')}
                  className="mt-6 text-sm text-white/40 hover:text-white transition-colors"
                >
                  &larr; Back to home
                </button>
              </motion.div>
            )}

            {view === 'signup' && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md bg-[rgba(16,21,32,0.8)] backdrop-blur-xl border border-white/10 rounded-2xl p-8 mb-12"
              >
                <h2 className="text-2xl font-space font-bold mb-6 text-white text-left">Create an account</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 text-left mb-2">Email</label>
                    <input 
                      type="email" 
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00f5ff] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 text-left mb-2">Password</label>
                    <input 
                      type="password" 
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00f5ff] transition-colors"
                    />
                  </div>
                  <button 
                    onClick={() => handleEmailAuth(true)}
                    disabled={!!isLoading}
                    className="w-full font-space py-3 rounded-xl btn-gradient font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    {isLoading === 'email' ? <span className="loader" /> : 'Sign Up'}
                  </button>

                  <button 
                    onClick={() => handleLogin('guest')}
                    disabled={!!isLoading}
                    className="w-full font-space py-2 text-sm text-white/60 hover:text-white uppercase tracking-[0.1em] transition-colors"
                  >
                    Or proceed as guest
                  </button>
                  
                  <div className="relative py-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-[rgba(16,21,32,1)] text-white/40">Or continue with</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <button 
                      onClick={() => handleLogin('google')}
                      disabled={!!isLoading}
                      className="w-full font-space py-3 rounded-xl btn-outline-gradient font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                      {isLoading === 'google' ? <span className="loader" /> : <><LogIn size={18} /> Google</>}
                    </button>
                    <button 
                      onClick={() => handleLogin('twitch')}
                      disabled={!!isLoading}
                      className="w-full font-space py-3 rounded-xl btn-twitch font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                      {isLoading === 'twitch' ? <span className="loader" /> : <><Twitch size={18} /> Twitch</>}
                    </button>
                  </div>
                </div>
                
                <button 
                  onClick={() => setView('home')}
                  className="mt-6 text-sm text-white/40 hover:text-white transition-colors"
                >
                  &larr; Back to home
                </button>
              </motion.div>
            )}

            {/* DOWNLOADS CTA */}
            {view === 'home' && (
              <div className="flex items-center justify-center gap-3 mb-12 flex-wrap text-sm">
                <span className="font-space text-xs uppercase tracking-[0.25em] text-white/40">or get the app</span>
                <a
                  href="https://github.com/Chimthuwu/PSYDE-ANDROID/releases/download/Android/Psydechat-Android.apk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-gradient font-space px-5 py-2.5 flex items-center gap-2 text-xs"
                >
                  <Smartphone size={16} className="text-[#00ff9d]" /> Android APK
                </a>
                <a
                  href="https://github.com/Chimthuwu/PSYDE-ANDROID/releases/download/Android/PsydeChat-Win64x-Setup1.0.0.exe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-gradient font-space px-5 py-2.5 flex items-center gap-2 text-xs"
                >
                  <Monitor size={16} className="text-[#00f5ff]" /> Windows Desktop
                </a>
              </div>
            )}

            {/* CLEAN HERO TEXT */}
            <motion.h1 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="font-space text-4xl md:text-5xl lg:text-7xl font-semibold mb-8 leading-[1.05]"
            >
              A next-generation<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff9d] to-[#00f5ff]">conversational experience</span>
            </motion.h1>

            <p className="max-w-3xl text-slate-300 text-lg md:text-xl leading-relaxed">
              Built on immersive design, living interfaces, and real-time adaptive visuals.
              Psydechat is a high-fidelity communication layer for presence, collaboration, and connection.
            </p>

            {error && (
              <div className="mt-8 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center gap-3 backdrop-blur-xl">
                <Shield size={18} />
                {error}
              </div>
            )}
          </div>
        </section>

        {/* FEATURES */}
        <section className="pb-32 px-6">
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              title="Chat System"
              desc="Create servers, build communities, and connect in real time with voice, video, and screenshare."
            />
            <FeatureCard 
              title="Voice Calls"
              desc="Peer-to-peer HD audio with spatial awareness and zero-configuration connection."
            />
            <FeatureCard 
              title="Adaptive UI"
              desc="Interfaces that respond dynamically to user interaction and motion context."
            />
          </div>
        </section>

        {/* FOOTER */}
        <footer className="text-center py-10 border-t border-white/10">
          <div className="flex items-center justify-center gap-6 mb-4 flex-wrap">
            <a
              href="https://github.com/Chimthuwu/PSYDE-ANDROID/releases/download/Android/Psydechat-Android.apk"
              target="_blank"
              rel="noopener noreferrer"
              className="font-space text-xs text-slate-400 hover:text-[#00f5ff] uppercase tracking-widest flex items-center gap-2 transition-colors"
            >
              <Download size={14} /> Download for Android
            </a>
            <a
              href="https://github.com/Chimthuwu/PSYDE-ANDROID/releases/download/Android/PsydeChat-Win64x-Setup1.0.0.exe"
              target="_blank"
              rel="noopener noreferrer"
              className="font-space text-xs text-slate-400 hover:text-[#00f5ff] uppercase tracking-widest flex items-center gap-2 transition-colors"
            >
              <Download size={14} /> Download for Windows
            </a>
          </div>
          <p className="font-space text-sm text-slate-400 tracking-widest uppercase">
            © 2026 PSYDECHAT. ALL RIGHTS RESERVED.
          </p>
        </footer>
      </main>
    </div>
  );
}

function FeatureCard({ title, desc }: { title: string, desc: string }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="glass-card p-10 rounded-3xl"
    >
      <h3 className="font-space text-2xl font-bold mb-4 text-white">{title}</h3>
      <p className="text-slate-400 leading-relaxed">{desc}</p>
    </motion.div>
  );
}
